import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import jwt_decode from 'jwt-decode';

import { __values } from 'tslib';
import { LoginSessionData } from './model';
import {
    decodeSessionFragment,
    isJwtExpired,
    scrubFragment,
    unwrap,
    WGINB_IDTOKEN_KEY,
    WGINB_USER_KEY
} from './sso-fragment.util';

@Injectable({ providedIn: 'root' })

export class SessionService {
    authStatus = new BehaviorSubject(false);

    constructor() { }

    /**
     * SSO Apps Portal handoff: if the URL carries a `#session=` fragment, hydrate the session
     * directly from it so the user lands logged in without re-clicking SSO. Synchronous — campus
     * fetches static data lazily elsewhere, so only the token + user are needed here. Runs as an
     * APP_INITIALIZER, before the auth guard. Returns false (falls through to login) when there is
     * no fragment, it is malformed, or the token is expired.
     */
    hydrateFromFragment(): boolean {
        const session = decodeSessionFragment();
        if (!session) {
            return false;
        }

        const idtoken = unwrap(session[WGINB_IDTOKEN_KEY]);
        const user = unwrap(session[WGINB_USER_KEY]);

        if (typeof idtoken !== 'string' || !user || isJwtExpired(idtoken)) {
            scrubFragment();
            return false;
        }

        this.setSessionObject({ idtoken, user } as LoginSessionData);
        scrubFragment();
        return true;
    }

    updateAuthStatus(status: boolean): void {
        this.authStatus.next(status);
    }

    setSessionObject(sessionData: LoginSessionData): void {
        sessionStorage.setItem("cb-user-session", JSON.stringify(sessionData));
        this.authStatus.next(true);
    }

    getSessionData(): LoginSessionData {
        const session: LoginSessionData = JSON.parse(sessionStorage.getItem('cb-user-session')!);
        return session;
    }

    getTokenData(value: keyof LoginSessionData | string): string {
        const session: LoginSessionData = JSON.parse(sessionStorage.getItem('cb-user-session')!);
        const tokenData: any = jwt_decode(session.idtoken);
        return tokenData[value];
    }

    isLoggedIn(): boolean {
        const session: LoginSessionData = JSON.parse(sessionStorage.getItem('cb-user-session')!);
        if (session === null) {
            return false;
        } else {
            const value: any = "exp";
            const timeExpired = new Date().getTime() > (parseInt(this.getTokenData(value), 10)) * 1000;
            return !timeExpired;
        }
    }

    clearSession(): void {
        sessionStorage.removeItem('cb-user-session');
        this.authStatus.next(false);
    }
}
